import type { ApiErrorBody } from "./types";

/**
 * Low-level HTTP client for Shalom App Control.
 *
 * Rules enforced here (see docs/SECURITY.md and docs/ARCHITECTURE.md):
 * - Same-origin requests only, credentials always included so the HttpOnly
 *   session cookie is sent. We never read or write the session cookie from
 *   JavaScript, and we never persist it or any token in localStorage.
 * - The CSRF token is kept in memory only (module-level variable) and
 *   attached as `X-CSRF-Token` on every mutating request, as required by
 *   docs/SECURITY.md. It is seeded by the auth endpoints that return it
 *   (TOTP / recovery-code verify) and refreshed opportunistically from
 *   `/api/auth/me` when the backend includes it there.
 * - Errors follow the `{ error: { code, message, details }, requestId }`
 *   shape from docs/API_CONTRACT.md. Messages shown to the user must stay
 *   generic for auth endpoints - the backend already avoids leaking account
 *   existence or which factor failed, and we must not reintroduce that leak
 *   client-side.
 */

const API_BASE = "/api";

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** AuthContext subscribes here to react to session loss from any request. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown> | undefined;
  readonly requestId: string | null;

  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, unknown> | undefined,
    requestId: string | null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Skip the global "session expired" broadcast, e.g. during login itself. */
  suppressUnauthorizedBroadcast?: boolean;
  signal?: AbortSignal;
}

async function parseErrorBody(response: Response): Promise<ApiErrorBody | null> {
  try {
    const data = (await response.json()) as ApiErrorBody;
    if (data && typeof data === "object" && "error" in data) {
      return data;
    }
    return null;
  } catch {
    return null;
  }
}

const GENERIC_ERROR_MESSAGE =
  "No se pudo completar la operacion. Intentalo de nuevo en unos instantes.";

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  let body: string | undefined;
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.body);
  }

  if (MUTATING_METHODS.has(method) && csrfToken) {
    headers["X-CSRF-Token"] = csrfToken;
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body,
      credentials: "include",
      signal: options.signal,
    });
  } catch {
    throw new ApiError(
      0,
      "network_error",
      "No se pudo conectar con el servidor. Comprueba tu conexion.",
      undefined,
      null,
    );
  }

  const requestId = response.headers.get("X-Request-Id");

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    const errorBody = await parseErrorBody(response);

    if (response.status === 401 && !options.suppressUnauthorizedBroadcast) {
      unauthorizedListeners.forEach((listener) => listener());
    }

    throw new ApiError(
      response.status,
      errorBody?.error.code ?? "unknown_error",
      errorBody?.error.message ?? GENERIC_ERROR_MESSAGE,
      errorBody?.error.details,
      errorBody?.requestId ?? requestId,
    );
  }

  const contentType = response.headers.get("Content-Type") ?? "";
  if (!contentType.includes("application/json")) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "POST", body }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "PATCH", body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, "method" | "body">) =>
    apiRequest<T>(path, { ...options, method: "DELETE" }),
};
